import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useCartDiscount, CartBeat } from '../../../hooks/useCartDiscount';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartBeat[];
  onRemoveItem: (id: string) => void;
}

export default function CartDrawer({ isOpen, onClose, cartItems, onRemoveItem }: CartDrawerProps) {
  const [email, setEmail] = useState('');
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [error, setError] = useState('');

  const {
    discountedItems,
    subtotal,
    totalDiscount,
    total,
    freeCount,
    bundleTier,
    paidItemsCount,
    nextTierNeeded,
  } = useCartDiscount(cartItems);

  const handleCheckout = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    if (cartItems.length === 0) {
      setError('Your cart is empty');
      return;
    }

    setIsCheckingOut(true);
    setError('');

    try {
      const { data, error: functionError } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          cartItems: discountedItems.map(item => ({
            id: item.id,
            title: item.isBundleFree ? `${item.title} (Bundle Free)` : item.title,
            price: item.discountedPrice,
          })),
          customerEmail: email,
        },
      });

      if (functionError) throw functionError;

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Failed to create checkout session. Please try again.');
      setIsCheckingOut(false);
    }
  };

  if (!isOpen) return null;

  const bundleLabel = bundleTier === 'ultimate'
    ? 'Ultimate Bundle — Buy 5, Get 3 FREE'
    : 'Triple Bundle — Buy 3, Get 1 FREE';

  const nextTierLabel = nextTierNeeded !== null
    ? paidItemsCount === 0
      ? `Add 3 beats to unlock 1 FREE`
      : paidItemsCount < 3
      ? `Add ${nextTierNeeded} more beat${nextTierNeeded > 1 ? 's' : ''} to get 1 FREE`
      : `Add ${nextTierNeeded} more beat${nextTierNeeded > 1 ? 's' : ''} to get 3 FREE (Ultimate Deal!)`
    : null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-gray-900">Your Cart</h2>
            {cartItems.length > 0 && (
              <span className="w-6 h-6 flex items-center justify-center bg-red-600 text-white text-xs font-bold rounded-full">
                {cartItems.length}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-900 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Bundle Progress Bar */}
        {cartItems.length > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
            {bundleTier ? (
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl border border-red-200">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-gift-2-fill text-red-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-xs font-bold text-red-700 uppercase tracking-wide">{bundleLabel}</p>
                  <p className="text-xs text-red-600 mt-0.5">
                    {freeCount} cheapest beat{freeCount > 1 ? 's are' : ' is'} FREE — saving you ${totalDiscount}!
                  </p>
                </div>
              </div>
            ) : nextTierLabel ? (
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border border-amber-200">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-flashlight-line text-amber-600 text-xl"></i>
                </div>
                <p className="text-xs font-semibold text-amber-800">{nextTierLabel}</p>
              </div>
            ) : null}
          </div>
        )}

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {cartItems.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
                <i className="ri-shopping-cart-line text-4xl text-gray-400"></i>
              </div>
              <p className="text-gray-600 mb-2 font-medium">Your cart is empty</p>
              <p className="text-xs text-gray-400 mb-6">Add beats to unlock bundle discounts</p>
              <button
                onClick={onClose}
                className="text-red-600 hover:text-red-700 font-medium text-sm whitespace-nowrap cursor-pointer"
              >
                Browse Beats
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {discountedItems.map(item => (
                <div
                  key={item.id}
                  className={`flex gap-4 rounded-xl p-4 border transition-all ${
                    item.isBundleFree
                      ? 'bg-red-50 border-red-200'
                      : 'bg-gray-50 border-gray-100'
                  }`}
                >
                  <img
                    src={item.waveformImage}
                    alt={item.title}
                    className="w-20 h-20 rounded-lg object-cover object-top flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-gray-900 text-sm leading-snug">{item.title}</h3>
                      {item.isBundleFree && (
                        <span className="flex-shrink-0 px-1.5 py-0.5 bg-red-600 text-white text-xs font-bold rounded-full whitespace-nowrap">
                          FREE
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{item.bpm} BPM • {item.key}</p>
                    <div className="flex gap-1 flex-wrap mt-1">
                      {item.genre.map(g => (
                        <span key={g} className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full">
                          {g}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      {item.isBundleFree ? (
                        <>
                          <span className="text-sm line-through text-gray-400">${item.originalPrice}</span>
                          <span className="text-sm font-bold text-red-600">$0</span>
                        </>
                      ) : (
                        <span className="text-base font-bold text-gray-900">${item.discountedPrice}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => onRemoveItem(item.id)}
                    className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600 transition-colors cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-delete-bin-line text-lg"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="border-t border-gray-200 p-6 space-y-4">
            {/* Email Input */}
            <div>
              <label htmlFor="checkout-email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="checkout-email"
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(''); }}
                placeholder="your@email.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                disabled={isCheckingOut}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <i className="ri-error-warning-line text-red-600 text-lg mt-0.5"></i>
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Pricing Summary */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal ({cartItems.length} beat{cartItems.length > 1 ? 's' : ''})</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              {totalDiscount > 0 && (
                <div className="flex justify-between font-semibold text-red-600">
                  <span className="flex items-center gap-1">
                    <i className="ri-gift-2-line text-base"></i>
                    Bundle Discount
                  </span>
                  <span>-${totalDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold border-t border-gray-100 pt-2 mt-1">
                <span className="text-gray-900">Total</span>
                <span className="text-red-600">${total.toFixed(2)}</span>
              </div>
            </div>

            {/* Checkout Button */}
            <button
              onClick={handleCheckout}
              disabled={isCheckingOut}
              className="w-full bg-red-600 text-white py-4 rounded-xl font-semibold hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              {isCheckingOut ? (
                <>
                  <i className="ri-loader-4-line text-xl animate-spin"></i>
                  Processing...
                </>
              ) : (
                <>
                  <i className="ri-secure-payment-line text-xl"></i>
                  Proceed to Checkout
                </>
              )}
            </button>

            <p className="text-xs text-center text-gray-500">
              Secure payment powered by Stripe
            </p>
          </div>
        )}
      </div>
    </>
  );
}
